import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  LinearProgress,
  Grid,
  Badge
} from '@mui/material';
import {
  Task as TaskIcon,
  Warning as WarningIcon,
  Schedule as ScheduleIcon,
  Add as AddIcon,
  KeyboardArrowRight as ArrowIcon,
  TrendingUp as TrendingUpIcon,
  Assignment as AssignmentIcon,
  NotificationImportant as UrgentIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';

// ToDo-Prioritäten-Konfiguration (inkl. kritisch)
const TODO_PRIORITAETEN = {
  kritisch: { color: '#d32f2f', icon: <UrgentIcon />, label: 'Kritisch' },
  hoch: { color: '#f44336', icon: <UrgentIcon />, label: 'Hoch' },
  mittel: { color: '#ff9800', icon: <WarningIcon />, label: 'Mittel' },
  niedrig: { color: '#4caf50', icon: <TaskIcon />, label: 'Niedrig' }
};

const TodoDashboardWidget = () => {
  const [dashboardData, setDashboardData] = useState({
    stats: {
      gesamt: 0,
      offen: 0,
      in_bearbeitung: 0,
      erledigt: 0,
      ueberfaellig: 0,
      diese_woche: 0,
      kritisch: 0,
      kuendigungen: 0,
      verhandlungen: 0,
    },
    anstehende_todos: []
  });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const data = await apiService.get('/api/vertrags-todos/dashboard');
      setDashboardData(data);
    } catch (error) {
      console.error('Fehler beim Laden der ToDo-Dashboard-Daten:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTageText = (tage) => {
    if (tage === 0) return 'Heute';
    if (tage === 1) return 'Morgen';
    if (tage > 0) return `in ${tage} Tagen`;
    if (tage === -1) return 'Gestern';
    return `vor ${Math.abs(tage)} Tagen`;
  };

  const getPrioritaetIcon = (prioritaet) => {
    return TODO_PRIORITAETEN[prioritaet] || TODO_PRIORITAETEN['niedrig'];
  };

  const handleTodoClick = (todoId) => {
    navigate(`/todo-management?highlight=${todoId}`);
  };

  // Abgeleitete Listen
  const { ueberfaelligeTodos, heuteFaelligeTodos, naechsteTodos } = useMemo(() => {
    const list = dashboardData.anstehende_todos || [];
    return {
      ueberfaelligeTodos: list.filter(t => typeof t.tage_bis_faellig === 'number' && t.tage_bis_faellig < 0),
      heuteFaelligeTodos: list.filter(t => t.tage_bis_faellig === 0),
      naechsteTodos: list.filter(t => typeof t.tage_bis_faellig === 'number' && t.tage_bis_faellig > 0)
    };
  }, [dashboardData]);

  const completionRate = dashboardData.stats.gesamt > 0 
    ? (dashboardData.stats.erledigt / dashboardData.stats.gesamt * 100)
    : 0;

  if (loading) {
    return (
      <Paper sx={{ p: 3, height: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <AssignmentIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6">ToDo-Übersicht</Typography>
        </Box>
        <LinearProgress />
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header mit Quick Stats */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <AssignmentIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6">ToDo-Übersicht</Typography>
        </Box>
        <Button
          size="small"
          endIcon={<ArrowIcon />}
          onClick={() => navigate('/todo-management')}
          sx={{ minWidth: 'auto' }}
        >
          Alle
        </Button>
      </Box>

      {/* Statistik-Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6}>
          <Card variant="outlined" sx={{ textAlign: 'center', py: 1.5 }}>
            <CardContent sx={{ p: '8px !important' }}>
              <Badge badgeContent={dashboardData.stats.ueberfaellig} color="error">
                <Typography variant="h4" color="primary">
                  {dashboardData.stats.offen}
                </Typography>
              </Badge>
              <Typography variant="caption" color="text.secondary">
                Offene ToDos
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6}>
          <Card variant="outlined" sx={{ textAlign: 'center', py: 1.5 }}>
            <CardContent sx={{ p: '8px !important' }}>
              <Typography variant="h4" color="warning.main">
                {heuteFaelligeTodos.length}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Heute fällig
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Fortschrittsbalken */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Abschlussrate
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {Math.round(completionRate)}%
          </Typography>
        </Box>
        <LinearProgress 
          variant="determinate" 
          value={completionRate} 
          sx={{ height: 8, borderRadius: 4 }}
        />
      </Box>

      {/* Überfällige ToDos (Priorität) */}
      {ueberfaelligeTodos.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="error" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
            <UrgentIcon sx={{ mr: 1, fontSize: 18 }} />
            Überfällig ({ueberfaelligeTodos.length})
          </Typography>
          <List dense sx={{ py: 0 }}>
            {ueberfaelligeTodos.slice(0, 3).map((todo) => (
              <ListItem 
                key={todo.id} 
                button 
                onClick={() => handleTodoClick(todo.id)}
                sx={{ 
                  px: 0, 
                  py: 0.5,
                  '&:hover': { backgroundColor: 'error.50' },
                  borderLeft: '3px solid',
                  borderLeftColor: 'error.main',
                  pl: 1,
                  mb: 0.5,
                  borderRadius: 1
                }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <Avatar sx={{ width: 24, height: 24, bgcolor: getPrioritaetIcon(todo.prioritaet).color }}>
                    {React.cloneElement(getPrioritaetIcon(todo.prioritaet).icon, { fontSize: 'small' })}
                  </Avatar>
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {todo.titel}
                    </Typography>
                  }
                  secondary={
                    <Typography variant="caption" color="error">
                      {getTageText(todo.tage_bis_faellig)}
                    </Typography>
                  }
                />
              </ListItem>
            ))}
          </List>
          {ueberfaelligeTodos.length > 3 && (
            <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>
              +{ueberfaelligeTodos.length - 3} weitere überfällige ToDos
            </Typography>
          )}
        </Box>
      )}

      {/* Heute fällige ToDos */}
      {heuteFaelligeTodos.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="warning.main" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
            <ScheduleIcon sx={{ mr: 1, fontSize: 18 }} />
            Heute fällig ({heuteFaelligeTodos.length})
          </Typography>
          <List dense sx={{ py: 0 }}>
            {heuteFaelligeTodos.slice(0, 2).map((todo) => (
              <ListItem 
                key={todo.id} 
                button 
                onClick={() => handleTodoClick(todo.id)}
                sx={{ 
                  px: 0, 
                  py: 0.5,
                  '&:hover': { backgroundColor: 'warning.50' },
                  borderLeft: '3px solid',
                  borderLeftColor: 'warning.main',
                  pl: 1,
                  mb: 0.5,
                  borderRadius: 1
                }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <Avatar sx={{ width: 24, height: 24, bgcolor: getPrioritaetIcon(todo.prioritaet).color }}>
                    {React.cloneElement(getPrioritaetIcon(todo.prioritaet).icon, { fontSize: 'small' })}
                  </Avatar>
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {todo.titel}
                    </Typography>
                  }
                  secondary={
                    <Typography variant="caption">
                      {todo.todo_typ} • {todo.anbieter}
                    </Typography>
                  }
                />
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {/* Nächste ToDos */}
      <Box sx={{ flexGrow: 1 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
          <TrendingUpIcon sx={{ mr: 1, fontSize: 18 }} />
          Nächste ToDos
        </Typography>
        <List dense sx={{ py: 0 }}>
          {naechsteTodos.slice(0, 4).map((todo) => (
            <ListItem 
              key={todo.id} 
              button 
              onClick={() => handleTodoClick(todo.id)}
              sx={{ 
                px: 0, 
                py: 0.5,
                '&:hover': { backgroundColor: 'action.hover' }
              }}
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                <Avatar sx={{ width: 24, height: 24, bgcolor: getPrioritaetIcon(todo.prioritaet).color }}>
                  {React.cloneElement(getPrioritaetIcon(todo.prioritaet).icon, { fontSize: 'small' })}
                </Avatar>
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="body2">
                    {todo.titel}
                  </Typography>
                }
                secondary={
                  <Typography variant="caption" color="text.secondary">
                    {getTageText(todo.tage_bis_faellig)} • {todo.todo_typ}
                  </Typography>
                }
              />
            </ListItem>
          ))}
        </List>
      </Box>

      {/* Footer Actions */}
      <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <Button
              fullWidth
              size="small"
              startIcon={<AddIcon />}
              onClick={() => navigate('/dashboard/todo-management?action=create')}
            >
              Neues ToDo
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Button
              fullWidth
              size="small"
              variant="outlined"
              endIcon={<ArrowIcon />}
              onClick={() => navigate('/dashboard/todo-management')}
            >
              Alle anzeigen
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
};

export default TodoDashboardWidget;